import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button } from '../common';
import currencyService from '../../services/currencyService';
import toast from 'react-hot-toast';
import { CalculatorIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

const MarginSettingsModal = ({ isOpen, onClose, rates, currencies, onSuccess }) => {
  const { t } = useTranslation();
  const [editedRates, setEditedRates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [marginMode, setMarginMode] = useState('percentage'); // 'percentage' or 'fixed'

  useEffect(() => {
    if (isOpen && rates) {
      setEditedRates(rates.map(r => {
        const spread = r.sellRate - r.buyRate;
        const marginPercent = r.buyRate > 0 ? ((r.sellRate - r.buyRate) / r.buyRate * 100) : 0;

        return {
          id: r.id,
          fromCurrencyId: r.fromCurrency.id,
          toCurrencyId: r.toCurrency.id,
          fromCode: r.fromCurrency.code,
          toCode: r.toCurrency.code,
          fromSymbol: r.fromCurrency.symbol,
          toSymbol: r.toCurrency.symbol,
          buyRate: r.buyRate.toString(),
          sellRate: r.sellRate.toString(),
          marginPercent: marginPercent.toFixed(4),
          spread: spread.toFixed(6),
          originalBuyRate: r.buyRate,
          originalSellRate: r.sellRate
        };
      }));
    }
  }, [isOpen, rates]);

  const handleBuyRateChange = (index, value) => {
    setEditedRates(prev => {
      const updated = [...prev];
      const buyRate = parseFloat(value) || 0;
      const marginPercent = parseFloat(updated[index].marginPercent) || 0;

      // Recalculate sell rate based on margin
      const sellRate = buyRate * (1 + marginPercent / 100);
      const spread = sellRate - buyRate;

      updated[index] = {
        ...updated[index],
        buyRate: value,
        sellRate: sellRate.toFixed(6),
        spread: spread.toFixed(6)
      };
      return updated;
    });
  };

  const handleMarginPercentChange = (index, value) => {
    setEditedRates(prev => {
      const updated = [...prev];
      const buyRate = parseFloat(updated[index].buyRate) || 0;
      const marginPercent = parseFloat(value) || 0;

      // Calculate new sell rate from margin percentage
      const sellRate = buyRate * (1 + marginPercent / 100);
      const spread = sellRate - buyRate;

      updated[index] = {
        ...updated[index],
        marginPercent: value,
        sellRate: sellRate.toFixed(6),
        spread: spread.toFixed(6)
      };
      return updated;
    });
  };

  const handleSpreadChange = (index, value) => {
    setEditedRates(prev => {
      const updated = [...prev];
      const buyRate = parseFloat(updated[index].buyRate) || 0;
      const spread = parseFloat(value) || 0;

      // Calculate new sell rate from spread
      const sellRate = buyRate + spread;
      const marginPercent = buyRate > 0 ? (spread / buyRate * 100) : 0;

      updated[index] = {
        ...updated[index],
        spread: value,
        sellRate: sellRate.toFixed(6),
        marginPercent: marginPercent.toFixed(4)
      };
      return updated;
    });
  };

  const handleSellRateChange = (index, value) => {
    setEditedRates(prev => {
      const updated = [...prev];
      const buyRate = parseFloat(updated[index].buyRate) || 0;
      const sellRate = parseFloat(value) || 0;

      // Recalculate margin and spread
      const spread = sellRate - buyRate;
      const marginPercent = buyRate > 0 ? (spread / buyRate * 100) : 0;

      updated[index] = {
        ...updated[index],
        sellRate: value,
        spread: spread.toFixed(6),
        marginPercent: marginPercent.toFixed(4)
      };
      return updated;
    });
  };

  const applyUniformMargin = (marginPercent) => {
    setEditedRates(prev => prev.map(rate => {
      const buyRate = parseFloat(rate.buyRate) || 0;
      const sellRate = buyRate * (1 + marginPercent / 100);
      const spread = sellRate - buyRate;

      return {
        ...rate,
        marginPercent: marginPercent.toFixed(4),
        sellRate: sellRate.toFixed(6),
        spread: spread.toFixed(6)
      };
    }));
  };

  const getChangedRates = () => {
    return editedRates.filter(r =>
      parseFloat(r.buyRate) !== r.originalBuyRate ||
      parseFloat(r.sellRate) !== r.originalSellRate
    );
  };

  const handleSubmit = async () => {
    const changedRates = getChangedRates();

    if (changedRates.length === 0) {
      toast.error(t('currencies.noChanges') || 'No changes to save');
      return;
    }

    // Validate rates
    for (const rate of changedRates) {
      const buy = parseFloat(rate.buyRate);
      const sell = parseFloat(rate.sellRate);
      if (isNaN(buy) || buy <= 0 || isNaN(sell) || sell <= 0) {
        toast.error(t('validation.positiveNumber'));
        return;
      }
      if (sell < buy) {
        toast.error(t('currencies.sellMustBeHigher') || `${rate.fromCode}/${rate.toCode}: Sell rate must be >= buy rate for profit`);
        return;
      }
    }

    setLoading(true);
    try {
      const ratesPayload = changedRates.map(r => ({
        fromCurrencyId: r.fromCurrencyId,
        toCurrencyId: r.toCurrencyId,
        buyRate: parseFloat(r.buyRate),
        sellRate: parseFloat(r.sellRate)
      }));

      const response = await currencyService.bulkUpdateRates(ratesPayload);

      if (response.success) {
        toast.success(t('currencies.marginUpdateSuccess') || `${changedRates.length} rate(s) updated successfully`);
        onSuccess && onSuccess();
        onClose();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const changedCount = getChangedRates().length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('currencies.marginSettings') || 'Profit Margin Settings'}
      size="xl"
    >
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('currencies.marginDescription') || 'Set profit margins for each currency pair. Sell rate will be calculated automatically.'}
          </p>

          {/* Quick margin buttons */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">{t('currencies.quickMargin') || 'Quick:'}</span>
            {[0.5, 1, 1.5, 2].map(m => (
              <button
                key={m}
                onClick={() => applyUniformMargin(m)}
                className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded transition-colors"
              >
                {m}%
              </button>
            ))}
          </div>
        </div>

        <div className="max-h-[400px] overflow-y-auto border rounded-lg dark:border-gray-700">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  {t('currencies.pair') || 'Pair'}
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  {t('currencies.buyRate')}
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  {t('currencies.margin') || 'Margin %'}
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  {t('currencies.spread') || 'Spread'}
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  {t('currencies.sellRate')}
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  {t('currencies.profitPer') || 'Profit/Unit'}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {editedRates.map((rate, index) => {
                const isChanged = parseFloat(rate.buyRate) !== rate.originalBuyRate ||
                                  parseFloat(rate.sellRate) !== rate.originalSellRate;
                const spread = parseFloat(rate.spread) || 0;
                const marginPercent = parseFloat(rate.marginPercent) || 0;
                const isNegativeMargin = marginPercent < 0;

                return (
                  <tr
                    key={rate.id}
                    className={`${isChanged ? 'bg-yellow-50 dark:bg-yellow-900/20' : ''} ${isNegativeMargin ? 'bg-red-50 dark:bg-red-900/20' : ''}`}
                  >
                    <td className="px-3 py-2 text-sm font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
                      <span className="bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                        {rate.fromCode}/{rate.toCode}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        step="0.000001"
                        value={rate.buyRate}
                        onChange={(e) => handleBuyRateChange(index, e.target.value)}
                        className="w-28 px-2 py-1 text-sm border rounded focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          step="0.01"
                          value={rate.marginPercent}
                          onChange={(e) => handleMarginPercentChange(index, e.target.value)}
                          className={`w-20 px-2 py-1 text-sm border rounded focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100 ${
                            isNegativeMargin ? 'border-red-400 bg-red-50 dark:bg-red-900/30' : ''
                          }`}
                        />
                        <span className="text-gray-400 text-xs">%</span>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        step="0.000001"
                        value={rate.spread}
                        onChange={(e) => handleSpreadChange(index, e.target.value)}
                        className="w-24 px-2 py-1 text-sm border rounded focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        step="0.000001"
                        value={rate.sellRate}
                        onChange={(e) => handleSellRateChange(index, e.target.value)}
                        className={`w-28 px-2 py-1 text-sm border rounded focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100 ${
                          isChanged ? 'border-yellow-400' : ''
                        }`}
                      />
                    </td>
                    <td className="px-3 py-2 text-sm whitespace-nowrap">
                      <span className={`font-medium ${spread >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                        {spread >= 0 ? '+' : ''}{spread.toFixed(4)} {rate.toCode}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-300 rounded"></div>
            <span>{t('currencies.modified') || 'Modified'}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-red-100 dark:bg-red-900/30 border border-red-300 rounded"></div>
            <span>{t('currencies.negativeMargin') || 'Negative margin (loss)'}</span>
          </div>
        </div>

        {changedCount > 0 && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-3 text-sm text-yellow-800 dark:text-yellow-200">
            <strong>{changedCount}</strong> {t('currencies.ratesWillBeUpdated') || 'rate(s) will be updated'}
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t dark:border-gray-700">
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={loading || changedCount === 0}>
            {loading ? (
              <>
                <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                {t('common.saving') || 'Saving...'}
              </>
            ) : (
              <>
                <CalculatorIcon className="h-4 w-4 mr-2" />
                {t('currencies.saveMargins') || 'Save Margins'}
              </>
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default MarginSettingsModal;
